import { apiFetch } from './client'
import type { Product, ProductCard, PaginatedResponse, DistributorOffer } from '../types'

export interface ProductQueryParams {
  category?: string
  subcategory?: string
  series?: string
  search?: string
  labels?: string[]
  page?: number
  page_size?: number
  lifecycle_phase?: string
  /** 3-state legacy (uitloop) visibility: 'all' (default), 'hide', 'only'. */
  legacy?: string
  sort?: string
  price_range?: string
  stock_status?: string
  delivery_speed?: string
  brand?: string
  /**
   * Per-category attribute filters — keyed by the raw `filterable_attributes`
   * key (e.g. "Number of channels") and listing the values the user
   * selected. Serialised as repeated `attribute[<key>][]=<value>` params,
   * one per value, matching how the URL is shaped by `<ProductFilters>`
   * and consumed by the backend `ProductFilterCriteria::fromRequest`.
   */
  attributes?: Record<string, readonly string[]>
}

export async function fetchProducts(
  params: ProductQueryParams = {},
): Promise<PaginatedResponse<ProductCard>> {
  const qs = new URLSearchParams()
  if (params.category) qs.set('category', params.category)
  if (params.subcategory) qs.set('subcategory', params.subcategory)
  if (params.series) qs.set('series', params.series)
  if (params.search) qs.set('search', params.search)
  if (params.labels && params.labels.length > 0) {
    params.labels.forEach(l => qs.append('labels', l))
  }
  if (params.page != null) qs.set('page', String(params.page))
  if (params.page_size != null) qs.set('page_size', String(params.page_size))
  if (params.lifecycle_phase) qs.set('lifecycle_phase', params.lifecycle_phase)
  if (params.legacy && params.legacy !== 'all') qs.set('legacy', params.legacy)
  if (params.sort) qs.set('sort', params.sort)
  if (params.price_range) qs.set('price_range', params.price_range)
  if (params.stock_status) qs.set('stock_status', params.stock_status)
  if (params.delivery_speed) qs.set('delivery_speed', params.delivery_speed)
  if (params.brand) qs.set('brand', params.brand)
  if (params.attributes) {
    for (const [attrKey, values] of Object.entries(params.attributes)) {
      if (!values || values.length === 0) continue
      const paramKey = `attribute[${attrKey}][]`
      for (const v of values) {
        if (!v) continue
        qs.append(paramKey, v)
      }
    }
  }

  const query = qs.toString()
  const res = await apiFetch<{ data: ProductCard[]; meta: { total: number; page: number; limit: number; pages: number } }>(`/products${query ? `?${query}` : ''}`)
  return {
    data: res.data,
    pagination: {
      page: res.meta.page,
      page_size: res.meta.limit,
      total: res.meta.total,
      total_pages: res.meta.pages,
      has_next: res.meta.page < res.meta.pages,
      has_prev: res.meta.page > 1,
    },
  }
}

export interface ProductDetailResponse {
  product: Product
  related: ProductCard[]
  distributor_offers: DistributorOffer[]
}

export async function fetchProductBySlug(slug: string): Promise<ProductDetailResponse> {
  const res = await apiFetch<{ data: Product; related: ProductCard[]; distributor_offers: DistributorOffer[] }>(`/products/${slug}`)
  return {
    product: res.data,
    related: res.related ?? [],
    distributor_offers: res.distributor_offers ?? [],
  }
}

export async function fetchLatestProducts(): Promise<ProductCard[]> {
  const res = await apiFetch<{ data: ProductCard[] }>('/products/featured')
  return res.data
}

/**
 * Fetch a hand-picked list of products by MPN, preserving the order of
 * the input list. Missing MPNs are silently dropped (never returned as
 * null/undefined), so callers can over-request — e.g. ask for 6 with the
 * intent to render the first 4 that resolve — without dealing with
 * placeholder rows.
 *
 * Used by the curated homepage FeaturedProducts component (see
 * [[homepage-focus]]). The backend caps the array at 50 entries; passing
 * more silently truncates server-side.
 */
export async function fetchProductsByMpns(
  mpns: readonly string[],
): Promise<ProductCard[]> {
  // Strip empties locally to keep the URL clean. Order matters — preserve it.
  const cleaned = mpns.map((m) => m.trim()).filter((m) => m.length > 0)
  if (cleaned.length === 0) return []

  const qs = new URLSearchParams()
  cleaned.forEach((m) => qs.append('mpn[]', m))
  qs.set('page_size', String(Math.min(cleaned.length, 50)))

  const res = await apiFetch<{ data: ProductCard[] }>(`/products?${qs.toString()}`)
  return res.data ?? []
}

export async function fetchTotalProductCount(): Promise<number> {
  const res = await fetchProducts({ page: 1, page_size: 1 })
  return res.pagination.total
}
